import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { FormGroup, FormRecord } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { Patient } from '../models/patient/patient.model';
import { PharmacySearchResponse } from '../models/pharmacy/pharmacy-search-response';
import { Prescriber } from '../models/prescriber/prescriber';
import { PrescriberSearchResponse } from '../models/prescriber/prescriber-search-response';
import { LandingProduct } from '../models/program-product-config.model';
import { Facility, HcoSearchResult } from '../models/search/seth-search-response.model';
import { PrescriberFromSearch } from '../models/prescriber/prescriber-from-search';
import { EnrollmentDocument } from '../models/enrollment.model';
import { MatDialogRef } from '@angular/material/dialog';
import { environment } from '../environments/environment';
import { PopupType } from '../models/enums/popup-type';
import { ContentType } from '../models/enums/content-type';
import { UserType } from '../models/enums/user-type.enum';
import { GetEntryRequest } from '../models/content/get-entry-request.model';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  _domParser = new DOMParser();
  _formData: FormRecord = new FormRecord({});
  patient$: WritableSignal<Patient> = signal({ firstName: '', lastName: '', sex: '', dob: '', primaryPhoneType: '', secondaryPhoneType: '', preferContactText: false });
  prescriber$: WritableSignal<Prescriber> = signal({
    firstName: '',
    lastName: '',
    address: {
      addressLine1: '',
      city: '',
      zipCode: '',
    },
    classification: '',
  });
  selectedPrescriberFromSearch$: WritableSignal<PrescriberFromSearch> = signal({
    referenceId: '',
    npi: '',
    classification: '',
    firstName: '',
    lastName: '',
    address: {
      referenceId: '',
      addressLine1: '',
      city: '',
      state: '',
      zipCode: '',
    },
    phone: '',
    fax: '',
    sortName: '',
    sortAddress: '',
  });
  initialDrugFamily$: WritableSignal<LandingProduct[]> = signal<LandingProduct[]>([]);
  products$: WritableSignal<string[]> = signal<string[]>([environment.LANDING_CONFIG.programProductConfig.at(0)?.products.at(0)?.display ?? '']);
  productNDCs$: WritableSignal<string[]> = signal<string[]>([]);
  plainTextProducts$: Signal<string[]> = computed(() => (this.products$().map(p => {
    const parsedProduct = this._domParser.parseFromString(p, 'text/html').body.textContent ?? p;    
    return parsedProduct.replace(/[^a-zA-Z0-9\s\-().'']/g, '');
  })));
  hasInsurance$: WritableSignal<boolean> = signal(false);
  hasInsuranceMM$: WritableSignal<boolean> = signal(false);
  hasInsurancePBM$: WritableSignal<boolean> = signal(false);
  _patientId = '' as string;
  _enrollmentId = '' as string;
  _programCaseId = '' as string;
  noPharmacies: PharmacySearchResponse = {
    recordsExceedMax: false, pharmacies: [{
      name: '--',
      storeNumber: '',
      npi: '--',
      ncpdpid: '',
      addressLine1: '--',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      fax: '',
      email: '',
      sortName: '',
    }],
  };
  noPrescribers: PrescriberSearchResponse = {
    recordsExceedMax: false,
    prescribers: [
      {
        referenceId: '',
        npi: '--',
        firstName: '',
        lastName: '',
        classification: '--',
        address: {
          addressLine1: '--',
          city: '',
          state: '',
          zipCode: '',
        },
        officeContactName: '',
        practiceName: '',
        prescriberEmail: '',
        groupTaxIdNumber: '',
      },
    ],
  };
  pharmacySearchResults: BehaviorSubject<PharmacySearchResponse> = new BehaviorSubject<PharmacySearchResponse>(this.noPharmacies);
  prescriberSearchResults: BehaviorSubject<PrescriberSearchResponse> = new BehaviorSubject<PrescriberSearchResponse>(this.noPrescribers);
  showPharmacyDetails$: WritableSignal<boolean> = signal<boolean>(false);
  private _emptyFacility: Facility = {
    name: '--',
    npi: '--',
    addressLine1: '--',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '--',
    fax: '',
    deaNumber: '',
    licenseNumber: '',
  };
  facility$: WritableSignal<Facility> = signal({
    name: '--',
    npi: '--',
    addressLine1: '--',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '--',
    fax: '',
    deaNumber: '',
    licenseNumber: '',
  });
  facilitySearchResults$: WritableSignal<HcoSearchResult> = signal({
    recordsExceedMax: false,
    facilities: [this._emptyFacility],
  });
  facilityPagination: WritableSignal<{ page: number, pageSize: number }> = signal({ page: 0, pageSize: 10 });
  prescriberPagination: WritableSignal<{ page: number, pageSize: number }> = signal({ page: 0, pageSize: 10 });
  pharmacyPagination: WritableSignal<{ page: number, pageSize: number }> = signal({ page: 0, pageSize: 10 });
  facilityAddedByUser$: WritableSignal<boolean> = signal(false);
  enrollmentDocuments$: WritableSignal<EnrollmentDocument[]> = signal<EnrollmentDocument[]>([]);
  matDialogRef$: WritableSignal<MatDialogRef<any, any> | null> = signal<MatDialogRef<any, any> | null>(null);

  workflow$: WritableSignal<string[]> = signal([UserType.prescriberInitiated]);
  formName$: WritableSignal<string | undefined> = signal(undefined);
  popupType$: WritableSignal<PopupType | undefined> = signal(undefined);
  contentType$: WritableSignal<ContentType> = signal(ContentType.depFeature);
  transactionId$: WritableSignal<string> = signal(this._enrollmentId);
  getEntryRequest$: Signal<GetEntryRequest> = computed(() => ({
    transactionId: this.transactionId$(),
    contentType: this.contentType$(),
    application: 'DEP',
    client: this.enrollmentMetadata$().client,
    program: this.enrollmentMetadata$().program,
    programCode: environment.PROGRAM_CODE,
    workflow: this.workflow$(),
    product: this.plainTextProducts$(),
    formName: this.formName$(),
    popupType: this.popupType$(),
    usePreviewApi: environment.USE_PREVIEW_API === 'true' ? true : false,
  }));
  enrollmentMetadata$: WritableSignal<{ client: string, program: string, programCode: string }> = signal({ client: environment.ENROLLMENT_OBJECT_METADATA.clientId, program: environment.ENROLLMENT_OBJECT_METADATA.engagementProgram, programCode: environment.ENROLLMENT_OBJECT_METADATA.engagementProgramCode });
  formNames = environment.FORM_NAMES;

  get patientId(): string {
    return this._patientId;
  }

  set patientId(patientId: string) {
    this._patientId = patientId;
  }

  get transactionId(): string {
    return this._enrollmentId;
  }

  set programCaseId(programCaseId: string) {
    this._programCaseId = programCaseId;
  }

  get programCaseId(): string {
    return this._programCaseId;
  }

  clearPrescriberSearchResults(): void {
    this.prescriberSearchResults.next(this.noPrescribers);
  }

  clearPharmacySearchResults(): void {
    this.pharmacySearchResults.next(this.noPharmacies);
  }

  createEnrollmentId(): void {
    this._enrollmentId = crypto.randomUUID();
  }

  setHasInsurance(state: boolean): void {
    this.hasInsurance$.set(state);
  }

  setMMInsurance(state: boolean): void {
    this.hasInsuranceMM$.set(state);
  }

  setPBMInsurance(state: boolean): void {
    this.hasInsurancePBM$.set(state);
  }

  setProducts(products: string[]): void {
    this.products$.set(products);
  }

  setProductNDCs(productNDCs: string[]): void {
    this.productNDCs$.set(productNDCs);
  }

  setInitialDrugFamily(products: LandingProduct[]): void {
    this.initialDrugFamily$.set(products);
  }

  getFormData(formName: string): FormGroup {
    return this._formData.get(formName) as FormGroup;
  }

  setFormData(formData: FormGroup, formName: string): void {
    this._formData.setControl(formName, formData.get(formName) as FormGroup);
  }

  resetGetEntryRequest(): void {
    this.transactionId$.set('');
    this.contentType$.set(ContentType.depFeature);
    this.workflow$.set([UserType.prescriberInitiated]);
    this.products$.set([environment.LANDING_CONFIG.programProductConfig.at(0)?.products.at(0)?.display ?? '']);
    this.formName$.set(undefined);
    this.popupType$.set(undefined);
  }

  clearSession(): void {
    this.resetGetEntryRequest();
    this._formData = new FormRecord({});
    this.patient$.set({ firstName: '', lastName: '', dob: '', sex: '', primaryPhoneType: '', secondaryPhoneType: '', preferContactText: false });
    this.prescriber$.set({
      firstName: '',
      lastName: '',
      address: {
        addressLine1: '',
        city: '',
        zipCode: '',
      },
      classification: '',
    })
    this.products$.set([]);
    this.productNDCs$.set([]);
    this._enrollmentId = '';
    this._patientId = '';
    //this.initialDrugFamily$.set([]);
    this.pharmacySearchResults.next(this.noPharmacies);
    this.prescriberSearchResults.next(this.noPrescribers);
    this.showPharmacyDetails$.set(false);
    this.facility$.set({
      name: '',
      npi: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      fax: '',
      deaNumber: '',
      licenseNumber: '',
    });
    this.facilitySearchResults$.set({
      recordsExceedMax: false,
      facilities: [],
    });
    this.facilityAddedByUser$.set(false);
    this.selectedPrescriberFromSearch$.set({
      referenceId: '',
      npi: '',
      classification: '',
      firstName: '',
      lastName: '',
      address: {
        referenceId: '',
        addressLine1: '',
        city: '',
        state: '',
        zipCode: '',
      },
      phone: '',
      fax: '',
      sortName: '',
      sortAddress: '',
    });
    this.prescriberPagination.set({ page: 0, pageSize: 10 });
    this.pharmacyPagination.set({ page: 0, pageSize: 10 });
    this.facilityPagination.set({ page: 0, pageSize: 10 });
    this.enrollmentDocuments$.set([]);
    if (this.matDialogRef$() && this.matDialogRef$()?.componentInstance) {
      this.matDialogRef$()?.close();
    }
  }

  resetSessionOnHomeComponent(): void {
    this._formData = new FormRecord({});
    this.patient$.set({ firstName: '', lastName: '', dob: '', sex: '', primaryPhoneType: '', secondaryPhoneType: '', preferContactText: false });
    this.prescriber$.set({
      firstName: '',
      lastName: '',
      address: {
        addressLine1: '',
        city: '',
        zipCode: '',
      },
      classification: '',
    })
    this._patientId = '';
    this.products$.set(this.initialDrugFamily$().map(product => product.display));
    this.productNDCs$.set([]);
    this.prescriberPagination.set({ page: 0, pageSize: 10 });
    this.pharmacyPagination.set({ page: 0, pageSize: 10 });
    this.facilityPagination.set({ page: 0, pageSize: 10 });
    this.facility$.set({
      name: '',
      npi: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      fax: '',
      deaNumber: '',
      licenseNumber: '',
    });
    this.facilitySearchResults$.set({
      recordsExceedMax: false,
      facilities: [],
    });    
  }
}
